import { useLayoutEffect, useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import type { RootState } from "@/store/store"
import { setAwaitingPaymentAttemptId, setAwaitingPaymentOrderId } from "@/slices/final-billing-slice"
import { getOwnedPaymentSession } from "@/domain/payment/payment-await-session"
import {
  storefrontBasePathFromPathname,
  storefrontJoin,
  stripStorefrontPrefix,
} from "@/utils/storefront-paths"
import { isDevelopmentOnlyLoggingEnabled } from "@/utils/development-only-logging"

const PAYMENT_CALLBACK_TYPE = "GMF_PAYMENT_CALLBACK"

function isAlreadyOnRecoverForOrder(
  pathname: string,
  search: string,
  orderId: string
): boolean {
  const appPath = stripStorefrontPrefix(pathname).replace(/\/+$/, "")
  if (appPath !== "/checkout/recover") return false
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
  const recoverOrderId = (params.get("orderId") ?? params.get("order_id") ?? "").trim()
  return recoverOrderId === orderId
}

/**
 * Listens for payment gateway postMessage callbacks while the app is open.
 *
 * Phase 2B Strategy B: message = wake verify only.
 * Does not settle success/failure; hands off to /checkout/recover for contract + is_paid.
 *
 * Payment-await session is restored into Redux only when
 * `payment_storefront_base` matches the current storefront. Foreign sessions
 * are ignored (not cleared) so the owning tenant can still recover.
 */
export function PaymentGatewayListener() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const awaitingOrderId = useSelector((s: RootState) => s.finalBilling.awaitingPaymentOrderId)
  const awaitingPaymentAttemptId = useSelector(
    (s: RootState) => s.finalBilling.awaitingPaymentAttemptId
  )
  const handledRef = useRef(false)
  const locationRef = useRef(location)
  locationRef.current = location

  useEffect(() => {
    handledRef.current = false
  }, [awaitingOrderId])

  /**
   * Restore payment await markers after refresh mid-redirect (sessionStorage).
   * Ownership is validated against the current storefront BEFORE any Redux write.
   */
  useLayoutEffect(() => {
    const currentBase = storefrontBasePathFromPathname(location.pathname)
    const owned = getOwnedPaymentSession(currentBase)

    if (!owned) {
      if (awaitingPaymentAttemptId?.trim()) {
        dispatch(setAwaitingPaymentAttemptId(null))
      }
      if (awaitingOrderId?.trim()) {
        dispatch(setAwaitingPaymentOrderId(null))
      }
      return
    }

    if (!awaitingPaymentAttemptId?.trim()) {
      dispatch(setAwaitingPaymentAttemptId(owned.paymentAttemptId))
    }
    if (!awaitingOrderId?.trim()) {
      dispatch(setAwaitingPaymentOrderId(owned.orderId))
    }
  }, [location.pathname, awaitingOrderId, awaitingPaymentAttemptId, dispatch])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      const d = event.data as {
        type?: string
        responseType?: string
        order_id?: string
      } | null

      if (!d || d.type !== PAYMENT_CALLBACK_TYPE) return

      const { pathname, search } = locationRef.current
      const currentBase = storefrontBasePathFromPathname(pathname)
      const owned = getOwnedPaymentSession(currentBase)
      if (!owned) return

      const oid = owned.orderId
      if (d.order_id && d.order_id !== oid) return

      if (handledRef.current) return

      handledRef.current = true

      if (isDevelopmentOnlyLoggingEnabled()) {
        // TEMP migration observability — wake only (no settle claims).
        // eslint-disable-next-line no-console -- development-only migration probe
        console.info("PAYMENT_LISTENER_WAKE", {
          orderId: oid,
          responseType: d.responseType ?? null,
        })
      }

      if (isAlreadyOnRecoverForOrder(pathname, search, oid)) {
        return
      }

      navigate(
        storefrontJoin(owned.storefrontBase, `/checkout/recover?orderId=${encodeURIComponent(oid)}`),
        { replace: true }
      )
    }

    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [navigate])

  return null
}
